import { router } from 'expo-router';
import React, { useState } from 'react';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    H2Emphasized,
    Icon,
    isWeb,
    Sheet,
    useToastController,
    View,
    WalletIcon,
    XStack,
    YStack,
} from 'smoothie';

import { AddToAppleWalletButton } from '../../banking/components/cards/AddToAppleWalletButton';
import { FruitfulCardIcon } from '../../banking/components/cards/FruitfulCard';
import { BankGeneralDisclosureFootnote } from '../../banking/components/Disclosures';
import { CardStatus } from '../../banking/types/Card';
import type { BankAccountAndCard } from '../../common/utils/bankingSwitchUtil';

export const BankingSwitchCompletedSheet = ({
    spendBankAccountAndCard,
    isOpen,
    onClose,
}: {
    spendBankAccountAndCard: BankAccountAndCard | undefined;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const insets = useSafeAreaInsets();
    const bottomMargin = isWeb ? 0 : insets.bottom;

    const cardForWallet = spendBankAccountAndCard?.card;

    const showAddToAppleWallet =
        cardForWallet?.cardStatus === CardStatus.ACTIVE &&
        Platform.OS === 'ios';

    return (
        <Sheet isOpen={isOpen} onClose={onClose} showCloseButton={true}>
            <YStack
                flex={1}
                paddingTop={0}
                width="100%"
                maxWidth={500}
                alignSelf="center"
            >
                <YStack
                    marginTop={50}
                    marginBottom={100}
                    width="100%"
                    maxWidth={400}
                    alignSelf="center"
                >
                    <View
                        alignSelf="center"
                        padding={25}
                        borderRadius={20}
                        backgroundColor="$green1000"
                        marginTop={30}
                        marginBottom={30}
                    >
                        <Icon icon={CheckIcon} color="$white" size={30} />
                    </View>
                    <H2Emphasized marginTop={16} textAlign="center">
                        Nicely done!
                    </H2Emphasized>
                    {cardForWallet != null && showAddToAppleWallet && (
                        <BodyMediumRegular
                            textAlign="center"
                            color="$gray1000"
                            marginTop={8}
                            maxWidth={320}
                            alignSelf="center"
                        >
                            You&apos;re all set. Add your card to Apple Wallet
                            so it&apos;s ready when you need it.
                        </BodyMediumRegular>
                    )}
                </YStack>
                <YStack gap={16} marginBottom={bottomMargin} width="100%">
                    <AddToWalletTile
                        showAddToAppleWallet={showAddToAppleWallet}
                        spendBankAccountAndCard={spendBankAccountAndCard}
                        onClose={onClose}
                    />
                    <BankGeneralDisclosureFootnote />
                </YStack>
            </YStack>
        </Sheet>
    );
};

const AddToWalletTile = ({
    showAddToAppleWallet,
    spendBankAccountAndCard,
    onClose,
}: {
    showAddToAppleWallet: boolean;
    spendBankAccountAndCard: BankAccountAndCard | undefined;
    onClose: () => void;
}) => {
    const toast = useToastController();

    const [showDoneButton, setShowDoneButton] = useState(false);

    const cardForWallet = spendBankAccountAndCard?.card;
    const nickname =
        spendBankAccountAndCard?.bankAccount?.nickname ?? 'Fruitful';

    const onAddToAppleWalletSuccess = () => {
        toast.show('Card added to Apple Wallet', {
            icon: (
                <View
                    backgroundColor="$green1000"
                    padding={16}
                    borderRadius={12}
                >
                    <Icon icon={WalletIcon} size={20} color="$white" />
                </View>
            ),
            duration: 2000,
        });
        setShowDoneButton(true);
    };

    return showDoneButton || cardForWallet == null ? (
        <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.LARGE}
            text="Back to Home"
            onPress={onClose}
        />
    ) : (
        <YStack
            width="100%"
            maxWidth={400}
            alignSelf="center"
            backgroundColor="$gray200"
            borderRadius={20}
            padding={20}
            gap={16}
        >
            <XStack gap={12} alignItems="center">
                <FruitfulCardIcon
                    form={cardForWallet.form}
                    color={cardForWallet.color}
                />
                <BodyMediumRegular flex={1}>
                    {`Add your ${nickname} card to ${showAddToAppleWallet ? 'Apple Wallet' : 'your digital wallet'} to start using it.`}
                </BodyMediumRegular>
            </XStack>
            {showAddToAppleWallet ? (
                <AddToAppleWalletButton
                    card={cardForWallet}
                    onSuccess={onAddToAppleWalletSuccess}
                    onError={() => {
                        toast.show('Failed to add card to Apple Wallet', {
                            duration: 2000,
                        });
                        throw new Error('Failed to add card to Apple Wallet');
                    }}
                    onProgress={() => {
                        // Show loading indicator
                        toast.show('Adding to Apple Wallet', {
                            duration: 2000,
                        });
                    }}
                />
            ) : (
                <YStack gap={8}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        text="Instructions for Apple Wallet"
                        onPress={() => {
                            router.navigate('/banking/card/apple-wallet');
                            onClose();
                        }}
                    />
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        text="Instructions for Google Pay"
                        onPress={() => {
                            router.navigate('/banking/card/google-pay');
                            onClose();
                        }}
                    />
                </YStack>
            )}
        </YStack>
    );
};
